import moment from 'moment';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { DataAddType } from 'src/common/enumerations/data-add-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import {
  CustomerNotFound,
  InvalidBarcode,
  RetailTransactionLogIsUnreachable,
  TBRTransactionNotFound,
  TransactionNotFound,
} from 'src/common/helpers/error-code';
import { LocalStorage } from 'src/common/helpers/local-storage';
import { IDataInformation } from 'src/common/interfaces/dtos/IDataInformation';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import {
  ITransactionSearchState,
  TransactionSearchContext,
} from 'src/common/interfaces/states/ITransactionSearchState';
import { store } from 'src/common/redux/configure-store';
import { TransactionSearchActions } from 'src/common/redux/reducers/transaction-search-reducer';
import { setLoyaltyCustomer } from 'src/customer-loyalty/business-logic/set-customer';
import { getDataInformation } from 'src/home-page/business-logic/get-data-infomation';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { handleReprintReceipt } from 'src/reprint-receipt/business-logic/handle-reprint-receipt';
import {
  returnTransactionClick,
  startReturnTypeTransaction,
} from 'src/return-services-display/business-logic/return-transaction';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import {
  DateRange,
  ReturnPolicyResponse,
} from '../interfaces/get-effective-return-policy-response';
import { SearchTransactionsResponse } from '../interfaces/transaction-search-response';
import {
  closeSearchTransactionPage,
  ISearchTransactionProps,
  showSearchTransactionPage,
} from '../search-transaction-page';
import { createNewDateRange } from '../transaction-search-helper/date-range-helper';
import {
  searchTransactionByBarcode,
  searchTransactionByParam,
} from '../transaction-search-helper/search-finished-transactions';
import {
  closeSearchModal,
  ISearchModalProps,
  showSearchModal,
} from '../transaction-search-screen-layout';

export const handleTransactionSearch = async (
  returnPolicy?: ReturnPolicyResponse,
): Promise<void> => {
  const oneMonth = 30;
  const longTime = 20000;
  const defaultStartDate =
    store.getState().transactionSearch.searchContext === TransactionSearchContext.TBR
      ? oneMonth
      : longTime;
  const defaultDateRange: DateRange = {
    startDate: createNewDateRange(
      returnPolicy ? returnPolicy.receiptLookupPeriod : defaultStartDate,
    ),
    endDate: moment().toDate(),
  };
  store.dispatch(
    TransactionSearchActions.setSearchParam({
      dateRange: defaultDateRange,
      storeNumber: LocalStorage.storeId(),
      isSuspended: false,
      isVoided: false,
    }),
  );
  const onKeypadSubmit = async (code: string): Promise<void> =>
    searchTransactionByCode(code, returnPolicy).catch(popupMessage);
  const props: ISearchTransactionProps = {
    onKeypadSubmit,
    onClose: closeSearchTransactionPage,
    completeTransactionSearch: () => completeTransactionSearch(returnPolicy),
  };

  showSearchTransactionPage(props, onKeypadSubmit);
};

export const completeTransactionSearch = (returnPolicy?: ReturnPolicyResponse): void => {
  const searchContext: TransactionSearchContext = store.getState().transactionSearch.searchContext;
  const onSearch = (itemCode?: string): Promise<void> =>
    searchTransaction(itemCode).catch(popupMessage);

  const onCancel = closeAllTransactionSearch;

  const onBack = (isResultView: boolean, hasCustomer: boolean = false): void => {
    resetSearchResult();
    if (hasCustomer || !isResultView) {
      closeSearchModal();
    }
  };

  const onSubmit = (tlogId: string): Promise<void> => {
    switch (searchContext) {
      case TransactionSearchContext.TBR: {
        return startTbrReturn(tlogId)
          .then(closeAllTransactionSearch)
          .catch(popupMessage);
      }
      case TransactionSearchContext.ReprintReceipt: {
        return handleReprintReceipt(tlogId);
      }
    }
  };

  const props: ISearchModalProps = {
    onSearch,
    onCancel,
    onBack,
    onSubmit,
    returnPolicy,
    searchContext,
  };

  showSearchModal(props, searchTransaction);
};

export const searchTransactionByCode = async (
  barcode: string,
  returnPolicy?: ReturnPolicyResponse,
  dataInformation?: IDataInformation,
): Promise<void> => {
  dataInformation = dataInformation || (await getDataInformation({ itemId: barcode }));
  const searchContext: TransactionSearchContext = store.getState().transactionSearch.searchContext;
  return searchTransactionByBarcode(
    dataInformation,
    { isSuspended: false, isVoided: false },
    searchContext,
  )
    .then(foundTransactions => processFoundTransactions(foundTransactions, returnPolicy, true))
    .catch(err => {
      resetSearchResult();
      switch (err.code) {
        case CustomerNotFound:
          throw new EmeraldError('TBR_InvalidLoyaltyCard');
        case TBRTransactionNotFound:
          if (dataInformation?.type === DataAddType.Customer) {
            throw new EmeraldError('TBR_NoTransactionLoyaltyCard');
          } else {
            throw nonReceiptReturnOptionModal(TBRTransactionNotFound);
          }
        case TransactionNotFound:
          if (dataInformation?.type === DataAddType.Customer) {
            throw new EmeraldError('TBR_NoTransactionLoyaltyCard');
          } else if (dataInformation?.type === DataAddType.Transaction) {
            throw new EmeraldError('TBRTransactionNotFound');
          }
          break;
        case InvalidBarcode:
          throw new EmeraldError('InvalidCardNumber');
        case RetailTransactionLogIsUnreachable:
          if (searchContext === TransactionSearchContext.TBR) {
            throw nonReceiptReturnOptionModal(RetailTransactionLogIsUnreachable);
          } else {
            throw new EmeraldError('CommunicationError');
          }

        default:
          throw err;
      }
    });
};

const searchTransaction = async (itemCode?: string): Promise<void> => {
  if (itemCode) {
    store.dispatch(
      TransactionSearchActions.setSearchParam({
        itemCode,
        pageNumber: 0,
      }),
    );
  }
  const transactionSearchState = store.getState().transactionSearch;
  const searchContext: TransactionSearchContext = store.getState().transactionSearch.searchContext;
  return searchTransactionByParam(transactionSearchState, searchContext)
    .then(processFoundTransactions)
    .catch(err => {
      if (err.code === 'EmeraldItemNotFound' && itemCode) {
        resetItemCode();
        throw new EmeraldError('ItemWasNotFoundInAnyOfTheRetrievedTransactions');
      }
      if (err.code === 'TransactionNotFound') {
        if (transactionSearchState.itemCode) {
          resetItemCode();
          throw new EmeraldError('ItemWasNotFoundInAnyOfTheRetrievedTransactions');
        }
        throw new EmeraldError('TBRTransactionNotFound');
      }
      if (err.code === RetailTransactionLogIsUnreachable) {
        if (searchContext === TransactionSearchContext.TBR) {
          throw nonReceiptReturnOptionModal(RetailTransactionLogIsUnreachable);
        } else {
          throw new EmeraldError(RetailTransactionLogIsUnreachable);
        }
      } else {
        throw err;
      }
    });
};

const processFoundTransactions = async (
  foundTransactions: SearchTransactionsResponse,
  returnPolicy?: ReturnPolicyResponse,
  searchByBarcode?: boolean,
): Promise<void> => {
  const searchContext: TransactionSearchContext = store.getState().transactionSearch.searchContext;
  const loyaltyCard = store.getState().transactionSearch.loyaltyCard;
  if (foundTransactions.pageContent.length) {
    if (foundTransactions.customerId) {
      store.dispatch(
        TransactionSearchActions.setSearchParam({
          customerId: foundTransactions.customerId,
        }),
      );
    }
    switch (searchContext) {
      case TransactionSearchContext.TBR: {
        if (foundTransactions.pageContent.length === 1) {
          return startTbrReturn(foundTransactions.pageContent[0].tlogId).then(
            closeAllTransactionSearch,
          );
        } else {
          completeTransactionSearch(returnPolicy);
          setSearchResultView(foundTransactions);
        }
        break;
      }
      case TransactionSearchContext.ReprintReceipt:
        if (foundTransactions.pageContent.length === 1 && !loyaltyCard && searchByBarcode) {
          return handleReprintReceipt(foundTransactions.pageContent[0].tlogId);
        } else {
          completeTransactionSearch(returnPolicy);
          setSearchResultView(foundTransactions);
        }
        break;
    }
  } else {
    throw new EmeraldError(
      searchContext === TransactionSearchContext.TBR ? TBRTransactionNotFound : TransactionNotFound,
    );
  }
};

const setSearchResultView = (foundTransactions: SearchTransactionsResponse): void => {
  const { pageNumber, transactionList } = store.getState().transactionSearch;
  const pageContent =
    pageNumber === 0 || !transactionList?.pageContent
      ? foundTransactions.pageContent
      : (transactionList?.pageContent).concat(foundTransactions.pageContent);
  store.dispatch(
    TransactionSearchActions.setSearchParam({
      isSearchResultView: true,
      transactionList: { ...foundTransactions, pageContent },
      customerId: foundTransactions.customerId,
    }),
  );
};

const resetSearchResult = (): void => {
  const transactionSearchState: Partial<ITransactionSearchState> = {
    isSearchResultView: false,
    transactionList: undefined,
    itemCode: undefined,
    customerId: undefined,
    isSuspended: false,
    isVoided: false,
    pageNumber: 0,
    loyaltyCard: undefined,
  };
  store.dispatch(TransactionSearchActions.setSearchParam(transactionSearchState));
};

const resetItemCode = (): void => {
  const transactionSearchState: Partial<ITransactionSearchState> = {
    itemCode: undefined,
  };
  store.dispatch(TransactionSearchActions.setSearchParam(transactionSearchState));
};

const startTbrReturn = async (tlogId: string): Promise<void> => {
  const consumerAccountNumber = store.getState().transactionSearch.customerId;
  const loyaltyId = store.getState().transactionSearch.loyaltyCard || undefined;
  return startReturnTypeTransaction(ReturnsType.TransactionBasedReturn, undefined, tlogId)
    .then(() => {
      if (consumerAccountNumber) {
        return setLoyaltyCustomer(
          {
            consumerAccountNumber: consumerAccountNumber,
            identifiers: [{ fieldName: 'loyaltyCard', fieldValue: loyaltyId }],
          },
          'Keyed',
          false,
        );
      }
    })
    .catch(err => {
      switch (err.code) {
        case 'NoSellableItemsFound.':
          throw new EmeraldErrorModal(noneSaleReturnModal);
        case 'NoItemsToReturn.':
          throw new EmeraldErrorModal(allItemReturnedErrorModal);
        default:
          throw err;
      }
    });
};

export const closeAllTransactionSearch = (): void => {
  resetSearchResult();
  closeSearchModal();
  closeSearchTransactionPage();
};

export const nonReceiptReturnOptionModal = (errorCode: string): EmeraldErrorModal => {
  const onAccept = (): void => {
    closeAllTransactionSearch();
    returnTransactionClick();
  };

  return new EmeraldErrorModal({
    type: EmeraldDialogTypes.Error,
    message: errorCode,
    onAccept,
    onCancel: () => {},
    disableBackdropClick: true,
    acceptButtonLabel: 'TransactionReturn_NonReceiptedReturn',
    closeButtonLabel: 'Cancel',
  });
};

const noneSaleReturnModal: IMessageModal = {
  type: EmeraldDialogTypes.Error,
  message: 'Error_TBR_Return_transaction',
  subMessage: 'Error_TBR_Return_Transaction_Subtext',
  acceptButtonLabel: 'OK',
  hideCancelButton: true,
};

const allItemReturnedErrorModal: IMessageModal = {
  type: EmeraldDialogTypes.Error,
  message: 'AllItemsAlreadyReturnedShort',
  subMessage: 'AllItemsAlreadyReturnedLong',
  acceptButtonLabel: 'OK',
  hideCancelButton: true,
};
